import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Label,
  Form,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import "react-toastify/dist/ReactToastify.css";

import { useToast } from "../../Components/Common/ToastContext";
import { updateSettingsKey, viewSettingsMore } from "./_requests";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const CompanySettings = () => {
  document.title = "Paal Dappa | Company Settings";

  const { id } = useParams();
  const { showToast } = useToast();
  const history = useNavigate();
  const [settingsMore, setSettingsMore] = useState<any>({});

  useEffect(() => {
    if (id) {
      viewSettingsMore(id).then((res: any) => {
        setSettingsMore(res);
      });
    }
  }, [id]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: settingsMore.name || "",
      email: settingsMore.email || "",
      mobile_number: settingsMore.mobile_number || "",
      address: settingsMore.address || "",
      gst_no: settingsMore.gst_no || "",
      bank_name: settingsMore.bank_name || "",
      account_no: settingsMore.account_no || "",
      branch: settingsMore.branch || "",
      ifsc_code: settingsMore.ifsc_code || "",
      update_type: 2
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Company Name"),
      email: Yup.string().email().required("Please Enter Company Email"),
      mobile_number: Yup.string().required("Please Enter Mobile Number"),
      address: Yup.string().required("Please Enter Address"),
    }),
    onSubmit: async (values) => {
      try {
        const res: any = await updateSettingsKey(id, values);
        if (res.status === true || res.data?.status === true) {
          toast.success(res.message || "Company settings updated successfully", { autoClose: 3000 });
          history("/settings", { state: { activeTab: "5" } });
        } else {
          toast.error(res.message || "Failed to update company settings", { autoClose: 3000 });
        }
      } catch (error: any) {
        toast.error(error.message || "An error occurred", { autoClose: 3000 });
      }
    },
  });

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb title="Company Settings" pageTitle="Paal Dappa" />
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Edit Company & Bank Details</h4>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <h5 className="mb-3 text-primary">Company Info</h5>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">Company Name</Label>
                        <Input
                          name="name"
                          type="text"
                          placeholder="Enter Company Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.name || ""}
                          invalid={
                            validation.touched.name && validation.errors.name ? true : false
                          }
                        />
                        {validation.touched.name && validation.errors.name ? (
                          <FormFeedback type="invalid">{validation.errors.name as string}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">Email</Label>
                        <Input
                          name="email"
                          type="email"
                          placeholder="Enter Company Email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email && validation.errors.email ? true : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">{validation.errors.email as string}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">Mobile Number</Label>
                        <Input
                          name="mobile_number"
                          type="text"
                          placeholder="Enter Mobile Number"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.mobile_number || ""}
                          invalid={
                            validation.touched.mobile_number && validation.errors.mobile_number ? true : false
                          }
                        />
                        {validation.touched.mobile_number && validation.errors.mobile_number ? (
                          <FormFeedback type="invalid">{validation.errors.mobile_number as string}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">GST Number</Label>
                        <Input
                          name="gst_no"
                          type="text"
                          placeholder="Enter GST Number"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.gst_no || ""}
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <Label className="form-label">Address</Label>
                        <Input
                          name="address"
                          type="textarea"
                          rows="3"
                          placeholder="Enter Address"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.address || ""}
                          invalid={
                            validation.touched.address && validation.errors.address ? true : false
                          }
                        />
                        {validation.touched.address && validation.errors.address ? (
                          <FormFeedback type="invalid">{validation.errors.address as string}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>

                  <hr className="my-4" />
                  <h5 className="mb-3 text-primary">Bank Details</h5>
                  
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">Bank Name</Label>
                        <Input
                          name="bank_name"
                          type="text"
                          placeholder="Enter Bank Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.bank_name || ""}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">Account Number</Label>
                        <Input
                          name="account_no"
                          type="text"
                          placeholder="Enter Account Number"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.account_no || ""}
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">Branch</Label>
                        <Input
                          name="branch"
                          type="text"
                          placeholder="Enter Branch Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.branch || ""}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">IFSC Code</Label>
                        <Input
                          name="ifsc_code"
                          type="text"
                          placeholder="Enter IFSC Code"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.ifsc_code || ""}
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className="text-end mt-4">
                    <Button type="submit" color="success" className="me-2 w-md">
                      Update Settings
                    </Button>
                    <Button type="button" color="light" className="w-md" onClick={() => history("/settings")}>
                      Cancel
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CompanySettings;
